type ITask = (...params: any[]) => void;

// longTask模式
export enum LongTaskModel {
    QUICK = 16 * 4, // 快但会降低fps
    SMOOTH = 16, // 慢但fps高
}

// 是否启用异步模式（每个task都开启一个宏任务）
const ASYNC_MODE = false;

/**
 * 纯净的任务队列，同步执行队列中的任务，但每隔固定周期会暂停任务执行，延迟到下个宏任务继续，用以维持高fps
 */
class PureTaskQueue {
    // 任务同步执行最长的周期，超过则暂停任务队列，下个宏任务重新启动
    private taskMaxRunTime = LongTaskModel.QUICK;
    // 任务队列 用map存储以给各任务绑定一个句柄key
    private queue: Map<number, ITask> = new Map();
    // 自增的number，每个任务赋值一个作为key
    private handleCounter = 0;
    // 当前任务队列是否处于执行状态
    private isProcessing = false;
    // 当前任务队列是否处于执行较长时间被中断期间（用以保证较高的fps）
    private isLongTaskBreakTime = false;
    // 异步模式的timer存储
    private timerMap = {};

    /**
     * 将任务添加到队列中
     * @param task
     */
    public add(task: ITask): number {
        this.handleCounter += 1;
        if (ASYNC_MODE) {
            this.timerMap[this.handleCounter] = setTimeout(() => task(), 0);
        } else {
            this.queue.set(this.handleCounter, task);
            if (!this.isProcessing && !this.isLongTaskBreakTime) {
                this.processQueue();
            }
        }
        return this.handleCounter;
    }

    /**
     * 从队列中移除任务
     * @param handleKey
     */
    public remove(handleKey: number): void {
        if (ASYNC_MODE) {
            if (this.timerMap[handleKey]) {
                clearTimeout(this.timerMap[handleKey]);
            }
        } else {
            this.queue.delete(handleKey);
        }
    }

    /**
     * 切换longTask模式
     * @param limit
     */
    public switchLongTaskLimit(limit: LongTaskModel): void {
        // 首屏所有缩略图出来前，任务最密集，且用户不太可能有复杂操作，所以加大单个任务长度，避免频繁切换，从而让页面尽快加载完成
        // 首屏均完成后，则缩减为16，以保证用户操作的高fps
        this.taskMaxRunTime = limit;
    }

    /**
     * 执行队列中的任务
     * @private
     */
    private processQueue(): void {
        this.isProcessing = true;
        const startTime = performance.now();
        while (this.queue.size > 0) {
            const [handleKey, task] = this.queue.entries().next().value;
            task();
            this.queue.delete(handleKey);
            const currentTime = performance.now();
            // 当同步任务的执行超过一定阈值时，则暂停任务，在下一个宏任务再继续执行，用来维持较高的fps
            if (currentTime - startTime >= this.taskMaxRunTime) {
                this.isLongTaskBreakTime = true;
                setTimeout(() => {
                    this.isLongTaskBreakTime = false;
                    this.processQueue();
                }, 0);
                break;
            }
        }
        this.isProcessing = false;
    }
}

export default new PureTaskQueue();
